/**
 * A score as a list of notes with times on them.
 *
 * This is the whole of playback that is worth testing: pure arithmetic from a document to
 * "this pitch, at this second, for this long, this loud". No audio is made here and
 * nothing in this file knows what a browser is, so the same schedule serves a WebAudio
 * player, a MIDI file writer, a test, or a cursor that follows the music on the page.
 *
 * Four things in the notation are not what they look like, and each is handled once, here
 * or in `schedule-part.ts`:
 *
 * - A **transposing part** is written where it is read and sounds somewhere else, so every
 *   pitch goes through the part's transposition on the way out.
 * - A **tie** is two written notes and one sound, so a tied pair is emitted as a single
 *   note as long as both of them together. Only a tie that moves forward in time is joined:
 *   an overfull bar can push a note past the bar after it, and joining backwards there
 *   would give a note a negative length.
 * - A **grace note** has no time of its own; it takes it from the note it leans on, which
 *   here means the main note starts later and is shorter by exactly what the graces take.
 * - A **repeat** makes the same bar sound twice, so the schedule is built over the played
 *   order of bars and not over the written one.
 *
 * A fifth thing is not notation but performance: a staccato dot, a hairpin, a trill, a
 * tremolo, an arpeggio and a fermata ask a player for lengths, weights, notes and time that
 * nobody wrote down. What they ask for is guessed in `realize.ts` and `dynamics.ts`, kept
 * out of the document, and turned off in one place -- `marks` in {@link ScheduleOptions} --
 * for a caller that wants the written notes and nothing more.
 *
 * A fermata is the one of those that moves the clock rather than a note: everybody waits,
 * including the parts that did not write the sign. So the holds of every part are gathered
 * first, and the whole timeline is stretched at the end.
 */

import { PPQ, ticksOf } from "../document/duration";
import { capacityOf } from "../document/query";
import type { Score } from "../document/types";
import { playOrder } from "../document/repeats";
import { partNotes } from "./schedule-part";
import type { BarTime, Hold, PlayedNote, Schedule, ScheduleOptions } from "./played";

export type { PlayedNote, Schedule, ScheduleOptions } from "./played";

/** Seconds per tick at a stated speed. */
function secondsPerTick(quarterBpm: number): number {
  return 60 / (quarterBpm * PPQ);
}

/** A tempo mark read as quarter notes per minute, whatever note value it counts. */
function quarterBpm(bpm: number, unitTicks: number): number {
  return (bpm * unitTicks) / PPQ;
}

function barTimes(score: Score, options: ScheduleOptions): readonly BarTime[] {
  const order = playOrder(score, options.repeats ?? true);
  let rate = secondsPerTick(options.tempo ?? 120);
  let clock = 0;
  let beat = 0;
  const times: BarTime[] = [];

  for (const index of order) {
    const measure = score.measures[index];
    if (measure === undefined) continue;
    if (measure.tempo !== null) {
      rate = secondsPerTick(quarterBpm(measure.tempo.bpm, ticksOf(measure.tempo.unit)));
    }
    const capacity = capacityOf(score, index);
    times.push({ index, start: clock, ticks: beat, rate });
    clock += capacity * rate;
    beat += capacity;
  }

  return times;
}

/**
 * Every part's fermatas as one list of moments the piece waits at.
 *
 * Two parts holding the same beat is one hold and not two, and a part that writes the
 * longer fermata decides how long everyone waits -- which is also what happens in a room,
 * where the players watch whoever is holding the note.
 */
function merged(holds: readonly Hold[]): readonly Hold[] {
  const longest = new Map<number, Hold>();
  for (const hold of holds) {
    const held = longest.get(hold.at);
    if (held === undefined || hold.ticks > held.ticks) longest.set(hold.at, hold);
  }
  return [...longest.values()].sort((one, other) => one.at - other.at);
}

/**
 * A note through a piece that waits.
 *
 * A hold before the note pushes it later; a hold inside it makes it longer; a hold exactly
 * where it starts is the note being held, which is the same as being inside it. A note that
 * has already stopped when the music waits is neither moved nor stretched, and a slurred note
 * stops where it is written to, its `overlap` being the next note taking over: held through
 * the next note's fermata, it would sound under the whole of it.
 */
function waited(note: PlayedNote, holds: readonly Hold[], overlap: number): PlayedNote {
  const end = note.startTicks + note.durationTicks - overlap;
  let shift = 0;
  let shiftTicks = 0;
  let grow = 0;
  let growTicks = 0;

  for (const hold of holds) {
    if (hold.at < note.startTicks) {
      shift += hold.seconds;
      shiftTicks += hold.ticks;
    } else if (hold.at < end) {
      grow += hold.seconds;
      growTicks += hold.ticks;
    }
  }

  if (shift === 0 && grow === 0) return note;
  return {
    ...note,
    start: note.start + shift,
    duration: note.duration + grow,
    startTicks: note.startTicks + shiftTicks,
    durationTicks: note.durationTicks + growTicks,
  };
}

/**
 * A score as notes with times on them.
 *
 * The order of the answer is by part and then by time within it, not by time across the
 * whole piece, because a player schedules everything up front and a highlighter looks
 * notes up by id. A caller that needs strict time order sorts it.
 */
export function scheduleOf(score: Score, options: ScheduleOptions = {}): Schedule {
  const times = barTimes(score, options);
  const played = score.parts.map((part) => partNotes(score, part, times, options));
  const holds = merged(played.flatMap((part) => part.holds));
  const notes = played.flatMap((part) =>
    part.notes.map((note, index) => waited(note, holds, part.overlaps[index] ?? 0)),
  );

  const last = times[times.length - 1];
  const capacity = last === undefined ? 0 : capacityOf(score, last.index);
  const waiting = holds.reduce((total, hold) => total + hold.seconds, 0);
  const waitingTicks = holds.reduce((total, hold) => total + hold.ticks, 0);
  const seconds = last === undefined ? 0 : last.start + capacity * last.rate + waiting;
  const ticks = last === undefined ? 0 : last.ticks + capacity + waitingTicks;

  return {
    notes,
    seconds,
    ticks,
    bars: times.map((bar) => {
      const before = holds.filter((hold) => hold.at < bar.ticks);
      return {
        index: bar.index,
        start: bar.start + before.reduce((total, hold) => total + hold.seconds, 0),
        ticks: bar.ticks + before.reduce((total, hold) => total + hold.ticks, 0),
      };
    }),
  };
}
